import type { PhoneCountry } from "./PhoneCountry";

export interface PhoneNumberCountryMetadata {
	/** Regex pattern for validating the national part (without country code) */
	pattern: string;
	/** Array of possible lengths for the national number */
	possibleLengths: number[];
	/** Leading digit(s) to strip when converting from national to international format (e.g., "0" for Sweden) */
	leadingNationalDigits?: string;
}

/**
 * Preferred country for ambiguous calling codes shared by multiple countries.
 * When multiple countries share the same calling code, the country listed here is preferred during auto-detection.
 * For example, calling code "1" is shared by US, Canada, and 23 other NANP countries.
 */
export const preferredCountryForCallingCode: Record<string, string> = {
	"1": "US",
	"7": "RU",
	"44": "GB",
	"47": "NO",
	"212": "MA",
	"262": "RE",
	"590": "GP",
};

/**
 * Country-specific phone number metadata from Google's libphonenumber.
 * Leading digit information from https://www.sent.dm/resources.
 * Patterns are for the national phone number part (without country code)
 */
export const phoneNumberPatterns: Record<string, PhoneNumberCountryMetadata> = {
	SE: {
		pattern: "(?:[26]\\d\\d|9)\\d{9}|[1-9]\\d{8}|[1-689]\\d{7}|[1-4689]\\d{6}|2\\d{5}",
		possibleLengths: [6, 7, 8, 9, 10],
		leadingNationalDigits: "0",
	},
	NO: {
		pattern: "(?:0|[2-9]\\d{3})\\d{4}",
		possibleLengths: [5, 8],
	},
	DK: {
		pattern: "[2-9]\\d{7}",
		possibleLengths: [8],
	},
	FI: {
		pattern: "[1-35689]\\d{4}|7\\d{10,11}|(?:[124-7]\\d|3[0-46-9])\\d{8}|[1-9]\\d{5,8}",
		possibleLengths: [5, 6, 7, 8, 9, 10, 11, 12],
		leadingNationalDigits: "0",
	},
	IS: {
		pattern: "(?:38\\d|[4-9])\\d{6}",
		possibleLengths: [7, 9],
	},
	US: {
		pattern: "[2-9]\\d{9}",
		possibleLengths: [10],
	},
	GB: {
		pattern: "[1-357-9]\\d{9}|[18]\\d{8}|8\\d{6}",
		possibleLengths: [7, 9, 10],
		leadingNationalDigits: "0",
	},
};

/**
 * Strips leading national digits from a phone number (e.g., removes "0" prefix for Swedish numbers)
 * Only strips the leading digits if the number is long enough to be a complete phone number
 *
 * Supported countries with leading digits:
 * - SE (Sweden): strips "0"
 * - FI (Finland): strips "0"
 * - GB (United Kingdom): strips "0"
 *
 * Countries without leading digits (no stripping): NO, DK, IS, US
 * All other countries will have no stripping as their information is not currently used
 *
 * @param phoneNumber The phone number in national format
 * @param isoCountryCode2 The ISO country code (e.g., "SE")
 * @returns The phone number with leading national digits removed
 */
export function stripLeadingNationalDigits(phoneNumber: string, isoCountryCode2: string): string {
	if (!phoneNumber) {
		return phoneNumber;
	}

	const countryData = phoneNumberPatterns[isoCountryCode2];
	if (!countryData || !countryData.leadingNationalDigits) {
		return phoneNumber;
	}

	if (phoneNumber.length >= 4 && phoneNumber.startsWith(countryData.leadingNationalDigits)) {
		return phoneNumber.substring(countryData.leadingNationalDigits.length);
	}

	return phoneNumber;
}

/**
 * Builds a full international phone number by stripping leading national digits and prepending the calling code.
 * Use this before submitting a phone number to the backend to ensure a consistent format.
 *
 * @param nationalNumber The phone number in national format (may still contain a leading national digit)
 * @param isoCountryCode2 The ISO country code (e.g., "SE")
 * @param callingCode The international calling code (e.g., "46")
 * @returns The full international phone number (e.g., "+46701234567") or the national number as-is if no calling code
 */
export function buildInternationalPhoneNumber(
	nationalNumber: string,
	isoCountryCode2: string,
	callingCode: string | undefined,
): string {
	if (!callingCode || !nationalNumber) {
		return nationalNumber;
	}
	const stripped = stripLeadingNationalDigits(nationalNumber, isoCountryCode2);
	return `+${callingCode}${stripped}`;
}

/**
 * Returns true if the phone number starts with an international prefix (+ or 00).
 */
export function hasInternationalPrefix(phoneNumber: string): boolean {
	return phoneNumber.startsWith("+") || phoneNumber.startsWith("00");
}

export interface ParsedPhoneNumber {
	/** The country ISO code if found */
	isoCountryCode2?: string;
	/** The phone number without country code */
	nationalNumber: string;
	/** The original input that was parsed */
	originalInput: string;
}

/**
 * Parses a phone number that may contain a country code and extracts the country and national number
 * Supports both international format (+46...) and some common formats (0046...)
 *
 * @param phoneNumber The phone number to parse (may include country code)
 * @param countries Array of available countries with their phone country codes
 * @returns ParsedPhoneNumber object with extracted information
 */
export function parsePhoneNumberWithCountryCode(
	phoneNumber: string,
	countries: Pick<PhoneCountry, "IsoCode2" | "InternationalCallingCode">[],
): ParsedPhoneNumber {
	if (!phoneNumber) {
		return {
			nationalNumber: "",
			originalInput: phoneNumber,
		};
	}

	// Clean the input - remove dashes, spaces, and parentheses
	const cleanedNumber = phoneNumber.replace(/[-\s()]/g, "");

	// If it doesn't start with + or 00, it's likely a national number
	if (!hasInternationalPrefix(cleanedNumber)) {
		return {
			nationalNumber: cleanedNumber,
			originalInput: phoneNumber,
		};
	}

	// For numbers starting with "00" or "+", wait until we have at least 5 characters
	// This ensures we can capture the longest country codes (3 digits) properly
	if (cleanedNumber.length < 5) {
		return {
			nationalNumber: cleanedNumber,
			originalInput: phoneNumber,
		};
	}

	// Remove + or 00 prefix
	const numberWithoutPrefix = cleanedNumber.startsWith("+")
		? cleanedNumber.substring(1)
		: cleanedNumber.startsWith("00")
			? cleanedNumber.substring(2)
			: cleanedNumber;

	// Try to match against country codes, starting with longest codes first.
	// For codes of the same length, prefer the country from the priority map.
	const sortedCountries = countries
		.filter(c => c.InternationalCallingCode)
		.sort((a, b) => {
			const lengthDiff = b.InternationalCallingCode.length - a.InternationalCallingCode.length;
			if (lengthDiff !== 0) {
				return lengthDiff;
			}
			// Same length â€” prefer the priority country for this calling code
			const aIsPreferred = preferredCountryForCallingCode[a.InternationalCallingCode] === a.IsoCode2 ? 1 : 0;
			const bIsPreferred = preferredCountryForCallingCode[b.InternationalCallingCode] === b.IsoCode2 ? 1 : 0;
			return bIsPreferred - aIsPreferred;
		});

	for (const country of sortedCountries) {
		if (numberWithoutPrefix.startsWith(country.InternationalCallingCode)) {
			const nationalNumber = numberWithoutPrefix.substring(country.InternationalCallingCode.length);
			return {
				isoCountryCode2: country.IsoCode2,
				nationalNumber,
				originalInput: phoneNumber,
			};
		}
	}

	// If no country code found, return as national number
	return {
		nationalNumber: numberWithoutPrefix,
		originalInput: phoneNumber,
	};
}
